import { SHEET_ID } from '../config.js';
import { parseAmount } from '../utils/format.js';
import { parseGvizDate } from '../utils/date.js';

/**
 * Carga la hoja `COSTOS/GASTOS` vía gviz/tq y devuelve ambos desgloses
 * como arrays paralelos.
 *
 * Estructura esperada del Google Sheet:
 *   Cols A,B,C → COSTO ADS (Fecha · Descripción · Total)
 *   Cols D,E,F → GASTOS GENERALES (Fecha · Descripción · Total)
 *
 * Tolera filas vacías y montos en cualquier formato ("$670.00", "670", 670).
 *
 * @param {{gid: string}} sheet  Referencia a la hoja (gid).
 * @returns {Promise<{costoAds: Entry[], gastosGenerales: Entry[]}>}
 *   donde Entry = { fecha: Date|null, descripcion: string, total: number }
 */
export function fetchCostsRows(sheet) {
  if (!sheet || !sheet.gid) {
    return Promise.resolve({ costoAds: [], gastosGenerales: [] });
  }

  return new Promise((resolve, reject) => {
    const cbName = '__costsCb_' + Date.now() + '_' + Math.floor(Math.random() * 1e6);

    const cleanup = () => {
      try { delete window[cbName]; } catch (e) {}
      script.remove();
      clearTimeout(timer);
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Timeout cargando COSTOS/GASTOS'));
    }, 20000);

    window[cbName] = (data) => {
      cleanup();
      try {
        if (data && data.status === 'error') {
          const detail = (data.errors && data.errors[0] && (data.errors[0].detailed_message || data.errors[0].message)) || 'error desconocido';
          throw new Error(detail);
        }
        if (!data || !data.table) throw new Error('Respuesta inesperada de Google Sheets');

        // Cols esperadas: A=ADS Fecha, B=ADS Desc, C=ADS Total,
        //                 D=GG  Fecha, E=GG Desc, F=GG Total
        const rows = data.table.rows || [];
        const costoAds = [];
        const gastosGenerales = [];

        for (const row of rows) {
          const c = row.c || [];
          const cellVal = (i) => (c[i] && c[i].v !== undefined) ? c[i].v : null;
          const cellFmt = (i) => (c[i] && c[i].f) ? c[i].f : null;

          // COSTO ADS (cols 0-2)
          const adsFecha = parseGvizDate(cellVal(0));
          const adsDesc = String(cellVal(1) || '').trim();
          const adsTotal = parseMoney(cellVal(2), cellFmt(2));
          if (adsFecha || adsDesc || adsTotal) {
            costoAds.push({ fecha: adsFecha, descripcion: adsDesc, total: adsTotal });
          }

          // GASTOS GENERALES (cols 3-5)
          const ggFecha = parseGvizDate(cellVal(3));
          const ggDesc = String(cellVal(4) || '').trim();
          const ggTotal = parseMoney(cellVal(5), cellFmt(5));
          if (ggFecha || ggDesc || ggTotal) {
            gastosGenerales.push({ fecha: ggFecha, descripcion: ggDesc, total: ggTotal });
          }
        }

        resolve({ costoAds, gastosGenerales });
      } catch (e) {
        reject(e);
      }
    };

    const script = document.createElement('script');
    script.src = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json;responseHandler:${cbName}&gid=${encodeURIComponent(sheet.gid)}&_=${Date.now()}`;
    script.onerror = () => {
      cleanup();
      reject(new Error('No se pudo cargar COSTOS/GASTOS.'));
    };
    document.head.appendChild(script);
  });
}

function parseMoney(v, f) {
  if (typeof v === 'number') return v;
  if (v == null) return 0;
  return parseAmount(f || String(v));
}
